import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Save, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { toast } from "sonner";

const inputCls = "w-full bg-[#0B1228] border border-white/10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white rounded-lg px-4 py-3 text-sm outline-none";

export default function AdminPanelPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [settings, setSettings] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("settings");

  useEffect(() => {
    api.get("/admin/me")
      .then((r) => {
        if (r.data.csrf_token) sessionStorage.setItem("tp-admin-csrf", r.data.csrf_token);
        if (r.data.email) sessionStorage.setItem("tp-admin-email", r.data.email);
        setAuthed(true);
      })
      .catch(() => {
        sessionStorage.removeItem("tp-admin-csrf");
        navigate("/admin/login");
      });
  }, [navigate]);

  useEffect(() => {
    if (!authed) return;
    api.get("/site-settings").then(r => setSettings(r.data)).catch(() => {});
    api.get("/admin/leads").then(r => setLeads(r.data)).catch((e) => {
      if (e?.response?.status === 401) navigate("/admin/login");
    });
  }, [authed, navigate]);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch (e) { /* session already gone */ }
    sessionStorage.removeItem("tp-admin-csrf");
    sessionStorage.removeItem("tp-admin-email");
    navigate("/admin/login");
  };

  if (!authed) return null;

  return (
    <main className="pt-28 pb-16" data-testid="admin-panel-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-1">CONTROL</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-white">{t.admin.panelTitle}</h1>
            <div className="text-xs text-slate-400 mt-2 font-mono">{sessionStorage.getItem("tp-admin-email")}</div>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-4 py-2 rounded-lg text-sm">
            <LogOut size={14} />{t.admin.logout}
          </button>
        </div>

        <div className="inline-flex bg-white/5 border border-white/10 rounded-lg p-1 mb-6">
          <button data-testid="admin-tab-settings" onClick={() => setTab("settings")} className={`px-4 py-2 text-sm font-medium rounded-md ${tab === "settings" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>{t.admin.siteSettings}</button>
          <button data-testid="admin-tab-leads" onClick={() => setTab("leads")} className={`px-4 py-2 text-sm font-medium rounded-md ${tab === "leads" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>{t.admin.leads} <span className="ml-1 text-xs opacity-70">({leads.length})</span></button>
        </div>

        {tab === "settings" && settings && (
          <SettingsForm t={t} settings={settings} setSettings={setSettings} />
        )}
        {tab === "leads" && <LeadsTable leads={leads} />}
      </div>
    </main>
  );
}

const SettingsForm = ({ t, settings, setSettings }) => {
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/site-settings", settings);
      toast.success(t.admin.saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="glass rounded-2xl p-7 grid sm:grid-cols-2 gap-4" data-testid="admin-settings-form">
      <Field label="Company name" value={settings.company_name} onChange={upd("company_name")} testid="set-company" />
      <Field label="Tagline" value={settings.tagline || ""} onChange={upd("tagline")} testid="set-tagline" />
      <Field label="Email" value={settings.email} onChange={upd("email")} testid="set-email" />
      <Field label="Phone" value={settings.phone || ""} onChange={upd("phone")} testid="set-phone" />
      <Field label="Website" value={settings.website} onChange={upd("website")} testid="set-website" />
      <Field label="Location" value={settings.location} onChange={upd("location")} testid="set-location" />
      <div className="sm:col-span-2">
        <button data-testid="admin-save-btn" type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold px-5 py-3 rounded-lg shadow-[0_0_20px_rgba(37,99,235,0.4)]">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}{t.admin.save}
        </button>
      </div>
    </form>
  );
};

const LeadsTable = ({ leads }) => (
  <div className="glass rounded-2xl overflow-hidden" data-testid="admin-leads-table">
    {leads.length === 0 ? (
      <div className="p-10 text-center text-slate-400">No leads yet.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left px-5 py-3">When</th>
              <th className="text-left px-5 py-3">Type</th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Details</th>
            </tr>
          </thead>
          <tbody className="text-slate-200">
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3 text-slate-400 font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded text-[10px] font-mono ${l.type === "demo" ? "bg-blue-500/15 text-blue-300" : "bg-cyan-500/15 text-cyan-300"}`}>{l.type}</span></td>
                <td className="px-5 py-3">{l.name}</td>
                <td className="px-5 py-3"><a className="hover:text-cyan-300" href={`mailto:${l.email}`}>{l.email}</a></td>
                <td className="px-5 py-3 text-xs text-slate-400 max-w-md truncate">{l.message || l.business_type || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const Field = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-[10px] tracking-widest text-slate-400 font-bold uppercase">{label}</span>
    <input data-testid={testid} className={`${inputCls} mt-2`} value={value} onChange={onChange} />
  </label>
);
